import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from "react";
import { Skeleton } from 'primereact/skeleton';
import { formatDecimals, formatCurrencyExtended, formatCoolDownTime } from '../../utils';
import { Card as NextCard } from "@nextui-org/react";
import ContractContext from '../../ContractContext';
import { Tooltip, IconButton, Collapse } from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const LoadingContent = () => <Skeleton width="100%" height="35px" className="mb-2" />;
const LoadingTitle = () => <Skeleton width="70%" height="25px" className="mb-2" />;

const miniTokenCardStyle = (image) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between', // Align children from top to bottom
    alignContent: 'start',
    width: '100%',
    padding: '12px',
    //backgroundImage: `linear-gradient(263deg, rgb(255 255 255 / 85%), rgb(255 255 255 / 85%)), url(${image}), linear-gradient(153deg,rgb(255, 255, 255), rgb(255, 255, 255) 19%, rgb(255, 255, 255))`,
    backgroundImage: `linear-gradient(263deg, rgb(255 255 255 / 85%), rgb(255 255 255 / 85%)), linear-gradient(153deg,rgb(255, 255, 255), rgb(255, 255, 255) 19%, rgb(255, 255, 255))`,
    backgroundColor: 'white',
    //outline: '2px solid #EC599A', // Gradient from EC599A to 0066A4
    backgroundPosition: '0px 0px, 115% 50%, 0px 0px',
    backgroundSize: 'auto, auto 135%, auto',
    borderTopLeftRadius: '25px',
    borderTopRightRadius: '25px',
    borderBottomLeftRadius: '25px',
    borderBottomRightRadius: '25px',
    backgroundRepeat: 'repeat, repeat-y, repeat',
    backgroundAttachment: 'scroll, scroll, scroll',
    color: 'black',
});

const carouselTrackStyle = {
    display: 'flex',
    gap: '10px',
    animation: 'scroll 240s linear infinite', // Smooth continuous scrolling
};

const carouselContainerStyle = {
    display: 'flex',
    overflowX: 'hidden', // Hide overflow
    gap: '10px',
    padding: '20px 0',
    position: 'relative',
    scrollSnapType: 'x mandatory',
    justifyContent: 'center', // Center the carousel items
    //water - ocean like background gradient - very very light blue to white
    backgroundImage: 'linear-gradient(180deg, #E0F3FF 0%,  #E0F3FF 0%)',
    marginTop: '20px',
    marginBottom: '20px',
};

const carouselItemStyle = {
    minWidth: '200px',
    scrollSnapAlign: 'center',
    flexShrink: 0,
    transition: 'transform 0.3s ease', // Smooth transition for item scaling
};

const TokenCarousel = React.memo(({ tokens = [], selectedTokenIn, selectedTokenOut, onSelectToken, selectionType, getTokenLogoUrl, poolIndex }) => {
    const trackRef = useRef(null);
    const { contracts } = useContext(ContractContext);
    const [isHovered, setIsHovered] = useState(false);
    const [filteredTokens, setFilteredTokens] = useState([]);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [isExpanded, setIsExpanded] = useState(() => {
        //const isExpanded = localStorage.getItem(`TokenCarouselIsExpanded_pool_${poolIndex}`);
        const isExpanded = localStorage.getItem(`TokenCarouselIsExpanded_pool`);
        if (isExpanded === null) {
            //localStorage.setItem(`TokenCarouselIsExpanded_pool_${poolIndex}`, true);
            localStorage.setItem(`TokenCarouselIsExpanded_pool`, true);
            return true;
        }
        return isExpanded === 'true';
    });

    const storageKey = 'carouselScrollingPosition';
    const playType = poolIndex === 3 ? '🥩' : poolIndex === 4 ? '🔥' : poolIndex === 2 ? '💳' : poolIndex === 1 ? '🔄' : '💰';

    const filterTokensByPlayPlatform = useCallback(async () => {
        if (!contracts.floatiePlay || !tokens.length) return;

        const filtered = await Promise.all(tokens.filter(token => token.balanceOfHolder > 0).map(async (token) => {
            const rewards = await contracts.floatiePlay.methods.playPlatform(token.address, poolIndex).call();
            // Calculate reward rate and adjusted rewards
            const accuracy = parseFloat(rewards.poolSettings.accuracy) <= parseFloat(rewards.poolSettings.chance) ? 10000 : parseFloat(rewards.poolSettings.accuracy);
            const rewardRate = parseFloat(rewards.poolSettings.rewardRate) / accuracy;
            const adjustedRewardValue = Math.min(parseFloat(rewards.total) * rewardRate, parseFloat(rewards.total));
            const adjustedRewardsAvailableUSD = (adjustedRewardValue * parseFloat(token.price) / (10 ** token.decimals));
            const coolDownTime = rewards.poolSettings.coolDownTime;
            const requiredAmount = rewards.poolSettings.requiredAmount;
            const requiredAmountUSD = (parseFloat(requiredAmount) * parseFloat(token.price) / (10 ** token.decimals));
            const minimumHoldAmount = rewards.poolSettings.minimumHoldAmount;

            return {
                ...token,
                rewardsAvailable: adjustedRewardValue,
                rewardsAvailableUSD: adjustedRewardsAvailableUSD,
                chance: parseFloat(rewards.poolSettings.chance) / 100,
                coolDownTime: coolDownTime,
                requiredAmount: requiredAmount,
                requiredAmountUSD: requiredAmountUSD,
                minimumHoldAmount: minimumHoldAmount,
            };
        }));

        setFilteredTokens(filtered.filter(token => token.rewardsAvailable > 0));
    }, [contracts.floatiePlay, tokens, poolIndex]);


    useEffect(() => {
        filterTokensByPlayPlatform();
    }, [filterTokensByPlayPlatform]);

    const duplicatedTokens = useMemo(() => [...filteredTokens, ...filteredTokens], [filteredTokens]);

    const keyframes = `
        @keyframes scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
        }
    `;

    const setTrackWidth = useCallback(() => {
        if (trackRef.current) {
            const trackWidth = trackRef.current.scrollWidth * 20; // / 2;
            trackRef.current.style.width = `${trackWidth}px`;
        }
    }, [filteredTokens.length]);

    useEffect(() => {
        setTrackWidth();
    }, [setTrackWidth]);

    useEffect(() => {
        const savedPosition = localStorage.getItem(storageKey);
        if (savedPosition && trackRef.current) {
            trackRef.current.scrollLeft = parseFloat(savedPosition);
            setScrollPosition(parseFloat(savedPosition));
        } else {
            if (trackRef.current) {
                trackRef.current.scrollLeft = 0;
                setScrollPosition(0);
            }
        }
    }, [filteredTokens]);

    // useEffect(() => {
    //     const saveScrollPosition = () => {
    //         if (trackRef.current) {
    //             localStorage.setItem(storageKey, trackRef.current.scrollLeft);
    //             setScrollPosition(trackRef.current.scrollLeft);
    //         }
    //     };

    //     const track = trackRef.current;
    //     track.addEventListener('scroll', saveScrollPosition);

    //     return () => {
    //         track.removeEventListener('scroll', saveScrollPosition);
    //     };
    // }, []);

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    const handleShowCarousel = () => {
        setIsExpanded(prevState => {
            const newState = !prevState;
            // localStorage.setItem(`TokenCarouselIsExpanded_pool_${poolIndex}`, newState);
            localStorage.setItem(`TokenCarouselIsExpanded_pool`, newState);
            return newState;
        });
    };

    return (
        <>
            <style>{keyframes}</style>
           
            <IconButton
                onClick={handleShowCarousel}
                style={{ position: 'absolute', top: '10px', right: '10px', color: '#EC599A' }}
                aria-label={isExpanded ? "Hide Details" : "Show Details"}
            >
                {isExpanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <div style={carouselContainerStyle}>
                    <div
                        ref={trackRef}
                        style={{
                            ...carouselTrackStyle,
                            animationPlayState: isHovered ? 'paused' : 'running',
                            transition: 'animation 0.5s ease-in-out',
                        }}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        {duplicatedTokens.map((token, index) => (
                            <div
                                key={`${token.value}-${index}`}
                                style={carouselItemStyle}
                            >
                                <NextCard isPressable isHoverable onClick={() => { setIsHovered(false); onSelectToken(selectionType, token); }} style={miniTokenCardStyle(getTokenLogoUrl(token))}>
                                    <NextCard.Body style={{ padding: '3px', overflow: 'hidden' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '18px' }}>
                                            <img src={getTokenLogoUrl(token)} alt={token.label} style={{ width: '30px' }} />
                                            <span>{token.label}</span>
                                        </div>
                                        <div style={{ marginTop: '10px', fontSize: '14px', fontWeight: 'normal' }}>
                                            <div style={{
                                                display: 'flex', width: '100%',
                                                fontSize: '22px', //color: '#EC599A',
                                                fontWeight: 'bold',
                                            }}>
                                                🏆: {formatDecimals(token.rewardsAvailable, token.decimals)} </div>
                                            <div>{formatCurrencyExtended(token.rewardsAvailableUSD)}</div>
                                            <div
                                                style={{
                                                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                                                    width: '100%', marginTop: '10px', fontSize: '12px', fontWeight: 'normal',
                                                }}
                                            >
                                                <div>
                                                    🎯: {token.chance}% </div>
                                                {/* ⏱: {formatCoolDownTime(token.coolDownTime)} <br/> */}
                                                {playType}: {formatDecimals(token.requiredAmount, token.decimals)} {token.label} <br />
                                                {/* ({formatCurrencyExtended(token.requiredAmountUSD)}) */}
                                                {/* 💼: {formatDecimals(token.minimumHoldAmount, token.decimals)} {token.label} */}
                                            </div>
                                        </div>
                                    </NextCard.Body>
                                </NextCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Collapse>
        </>
    );
});

export default TokenCarousel;